import { useIntl } from 'react-intl';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { DataType } from '@/core/interfaces/common';
import { Breadcrumbs } from '@/core/components/Breadcrumbs';
import { MarginWrapper } from '@/core/components/MarginWrapper';
import {
  Subheader,
  Title,
  TitleWrapper,
} from '@/core/components/PageLayout';
import { articleMessages } from '@/core/messages/article.messages';
import { ResourceLink } from '@/core/components/ResourceLink';
import { ResourceType } from '@/core/components/ResourceLink/resourceLink.types';
import { RichText } from '@/core/components/RichText';
import { SocialShare } from '@/core/components/SocialShare';
import { useLocalizedOGTitle } from '@/core/hooks/useLocalizedOGTitle';
import { useGlobalContext } from '@/core/contexts/GlobalContext/GlobalContext';

import {
  getBackendImage,
  getKeywordsProps,
  getPageLink,
} from '@/utils/helpers';

import {
  Content,
  ContentWrapper,
  FeaturedArticles,
  ListWrapper,
  SocialShareWrapper,
} from './Article.styles';
import { Article as ArticleType } from '../Articles/Articles.types';
import { Banner } from './Banner';

export type ArticleProps = {
  articleData: DataType<ArticleType>;
};

export const Article = ({
  articleData: {
    attributes: {
      author,
      content,
      createdAt,
      featuredArticles,
      image,
      locale: articleLocale,
      localizations,
      meta,
      publishedAt,
      readingTime,
      slug,
      subtitle,
      title,
      topics,
    },
  },
}: ArticleProps) => {
  const { formatMessage } = useIntl();

  const { setArticleSlugs } = useGlobalContext();

  const {
    asPath,
    locale,
  } = useRouter();

  const OGTitle = useLocalizedOGTitle(meta?.OGTitle || meta?.title || title);

  useEffect(() => {
    const localizedSlugs = {
      [articleLocale]: slug,
      ...(localizations.data.reduce((acc, version) => {
        acc[version.attributes.locale] = version.attributes.slug;

        return acc;
      }, {} as { [key: string]: string })),
    };

    setArticleSlugs(localizedSlugs);

    return () => {
      setArticleSlugs({});
    };
  }, [
    articleLocale,
    slug,
    localizations,
    setArticleSlugs,
  ]);

  return (
    <>
      <NextSeo
        title={meta?.title || title}
        description={meta?.description || ''}
        openGraph={{
          article: {
            authors: [author?.data?.attributes?.name || ''],
            ...(meta?.keywords.length ?
              { tags: meta.keywords.map(keyword => keyword.keyword) } :
              {}
            ),
            publishedTime: publishedAt,
          },
          description: meta?.OGDescription || meta?.description || '',
          images: [
            {
              alt: image?.data?.attributes?.alternativeText || title,
              height: 600,
              type: 'image/jpeg',
              url: getBackendImage(image?.data?.attributes.url),
              width: 800,
            },
          ],

          title: OGTitle,
          type: 'article',
        }}
        {...getKeywordsProps(meta?.keywords)}
      />
      <Banner
        image={getBackendImage(image?.data?.attributes?.url)}
        alt={image?.data?.attributes?.alternativeText || title}
        title={title}
        subtitle={subtitle}
        author={author?.data?.attributes}
        topics={topics?.data}
        date={createdAt}
        readingTime={readingTime}
      />
      <Breadcrumbs currentPage={title} />
      <MarginWrapper>
        <ContentWrapper>
          <Content>
            <RichText
              content={content}
            />
            <SocialShareWrapper>
              <SocialShare link={getPageLink(asPath, locale || 'ar-AE')} />
            </SocialShareWrapper>
          </Content>
        </ContentWrapper>
      </MarginWrapper>
      {!!featuredArticles.data.length && (
        <FeaturedArticles>
          <MarginWrapper>
            <TitleWrapper>
              <Subheader>{formatMessage(articleMessages.subtitle)}</Subheader>
              <Title>{formatMessage(articleMessages.title)}</Title>
            </TitleWrapper>
            <ListWrapper>
              {featuredArticles.data.map(article => (
                <ResourceLink
                  type={ResourceType.ARTICLE}
                  key={article.attributes.slug}
                  data={{
                    attributes: {
                      date: article.attributes.createdAt,
                      image: article.attributes.image,
                      link: article.attributes.slug,
                      readingTime: article.attributes.readingTime,
                      subtitle: article.attributes.subtitle,
                      title: article.attributes.title,
                      topic: article.attributes.topics?.data[0]?.attributes?.name,
                    },
                    id: article.id,
                  }}
                />
              ))}
            </ListWrapper>
          </MarginWrapper>
        </FeaturedArticles>
      )}
    </>
  );
};
